import React, { useCallback, useEffect, useRef, useState } from "react";
import Icons from "../icons";
import { useTheme } from "../theme";
import TicketLinkField from "./TicketLinkField";
import { DIALOG_HEIGHT } from "./dialogSize";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

function apiWsUrl(path) {
  const base = new URL(API_BASE_URL, window.location.origin);
  const proto = base.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${base.host}${path}`;
}

const inputCls =
  "w-full rounded-lg border border-slate-300 bg-card px-3 py-2 text-sm text-slate-900 placeholder-slate-400 outline-none transition focus:border-brand-600 focus:ring-2 focus:ring-brand-600/25";

const DYNAMIC_ACTIONS = new Set([
  "fill",
  "type",
  "press",
  "selectOption",
  "expectText",
  "expectUrlContains",
]);

function canBeDynamic(s) {
  return DYNAMIC_ACTIONS.has(s.action) && s.value != null && s.value !== "";
}

function defaultParamName(s, index) {
  const src = (s.field_label || s.description || "").toString().trim();
  if (src) {
    const slug = src.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
    if (slug) return slug;
  }
  return `value_${index + 1}`;
}

// Rebuilds a plain-English step description with {{param}} where the literal
// value would normally appear. Kept in sync with the patterns emitted by
// PlantOPSCortexQA-API/app/services/recorder_service.py::_describe_fill.
function dynamicDescription(s, paramName) {
  const label = s.field_label || s.selector || "field";
  const placeholder = `{{${paramName || "value"}}}`;
  if (s.action === "selectOption" || (s.action === "fill" && s.kind === "select")) {
    return `Select ${placeholder} in ${label}`;
  }
  if (s.action === "fill" || s.action === "type") {
    return `Type ${placeholder} into ${label}`;
  }
  if (s.action === "press") {
    return `Press ${placeholder}`;
  }
  if (s.action === "expectText") {
    return `Expect text ${placeholder} in ${label}`;
  }
  if (s.action === "expectUrlContains") {
    return `Expect URL contains ${placeholder}`;
  }
  return s.description;
}

function legacyStepLabel(s) {
  const sel = s.selector ? ` ${s.selector}` : "";
  const shown = s.is_dynamic ? `{{${s.param_name || "value"}}}` : s.value;
  const val = shown != null && shown !== "" ? ` = "${shown}"` : "";
  return `${s.action}${sel}${val}`;
}

function stepLabel(s) {
  if (s.is_dynamic) {
    return dynamicDescription(s, s.param_name) || legacyStepLabel(s);
  }
  return s.description || legacyStepLabel(s);
}

export default function ManualTestCasePage({ board, subcategorykey = null, onCancel, onCreate, presetTickets = [], lockTickets = false, variant = "testcase" }) {
  // The name field always names a group of Playwright steps, whichever entry
  // point you came from (board, ticket or Explore & Author), so the wording is
  // the same everywhere. `variant` is kept for callers that pass it.
  const nameLabel = "Artifact Group Name";
  const { userkey } = useTheme();

  const [name, setName] = useState("");
  const [phase, setPhase] = useState("form"); // "form" | "starting" | "recording"
  const [sessionId, setSessionId] = useState(null);
  const [startError, setStartError] = useState("");

  const [frameUrl, setFrameUrl] = useState(null);
  const [openedUrl, setOpenedUrl] = useState("");
  const [recSteps, setRecSteps] = useState([]);
  const [infoLine, setInfoLine] = useState("");
  const [rtError, setRtError] = useState("");

  const [activeInputSelector, setActiveInputSelector] = useState(null);
  const [fillValue, setFillValue] = useState("");

  // Element-under-cursor highlight box (viewport ratios) for the live view.
  const [hoverBox, setHoverBox] = useState(null);

  const [autoLogin, setAutoLogin] = useState(true);
  const [maximized, setMaximized] = useState(false);

  const [credOpen, setCredOpen] = useState(false);
  const [credUser, setCredUser] = useState("");
  const [credPwd, setCredPwd] = useState("");
  const [credPwdVisible, setCredPwdVisible] = useState(false);
  const [sessionAutoLogin, setSessionAutoLogin] = useState(false);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  // Optional Jira tickets (Testing status) to link the saved case to.
  const [linkTickets, setLinkTickets] = useState(presetTickets);

  // ---- Xray manual import ----
  // Story key to discover Xray tests from (arrived from a ticket).
  const presetStoryKey = presetTickets?.[0]?.ticket_key || "";
  const [xrayData, setXrayData] = useState(null);
  const [xrayLoading, setXrayLoading] = useState(false);
  const [xrayError, setXrayError] = useState("");
  const [xrayChecked, setXrayChecked] = useState(false);
  // The single Xray Test the user chose to record against: {issue_id, key,
  // summary, steps:[{step_no, action, data, result}]}.
  const [selectedXrayTest, setSelectedXrayTest] = useState(null);
  // Which Xray step the tester is currently performing (index into steps).
  const [xrayStepIdx, setXrayStepIdx] = useState(0);
  // Steps already stashed against an Xray step (each carries source_step_no).
  const [committedSteps, setCommittedSteps] = useState([]);

  const wsRef = useRef(null);
  const imgRef = useRef(null);
  const viewportRef = useRef(null);
  const fillInputRef = useRef(null);
  const heartbeatRef = useRef(null);
  const hoverThrottleRef = useRef(0);

  const checkedCount = recSteps.filter((s) => s.checked).length;

  // ---- Xray helpers ----
  const xraySteps = selectedXrayTest?.steps || [];
  const currentXrayStep = xraySteps[xrayStepIdx] || null;
  const allXrayStepsCovered =
    !!selectedXrayTest &&
    xraySteps.every((xs) => committedSteps.some((s) => s.source_step_no === xs.step_no));

  const loadXrayTests = useCallback(async (storyKey) => {
    const q = (storyKey || "").trim();
    if (!q) return;
    setXrayLoading(true);
    setXrayError("");
    setXrayChecked(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}xray/discover?story=${encodeURIComponent(q)}`
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setXrayData(null);
        setXrayError(body?.detail || `HTTP ${res.status}`);
        return;
      }
      setXrayData(body?.data || null);
    } catch (e) {
      setXrayData(null);
      setXrayError(`Network error: ${e.message}`);
    } finally {
      setXrayLoading(false);
    }
  }, []);

  const pickXrayTest = (t) => {
    setSelectedXrayTest(t);
    setXrayStepIdx(0);
    setCommittedSteps([]);
    if (t?.summary && !name.trim()) setName(t.summary);
  };

  // Stash the current recorded buffer against the active Xray step, then
  // advance to the next step and clear the buffer. Only the CHECKED steps are
  // committed; the tester can uncheck noise (e.g. stray auto steps) first.
  const stashAgainstCurrentStep = () => {
    if (!currentXrayStep) return;
    const toCommit = recSteps
      .filter((s) => s.checked)
      .map((s) => ({ ...s, source_step_no: currentXrayStep.step_no }));
    if (toCommit.length === 0) {
      setSaveError("Nothing to stash — perform at least one step first.");
      return;
    }
    setSaveError("");
    setCommittedSteps((prev) => [...prev, ...toCommit]);
    setRecSteps([]); // empty the stash area
    setActiveInputSelector(null);
    // Advance to the next uncovered step (or stay at end).
    setXrayStepIdx((idx) => Math.min(idx + 1, Math.max(xraySteps.length - 1, 0)));
  };

  const unstashGroup = (stepNo) => {
    setCommittedSteps((prev) => prev.filter((s) => s.source_step_no !== stepNo));
  };

  // ---------- websocket lifecycle ----------

  const openWs = useCallback((sid) => {
    const ws = new WebSocket(apiWsUrl(`/api/ws/recorder/${sid}`));
    wsRef.current = ws;
    ws.onopen = () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      heartbeatRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          try {
            ws.send(JSON.stringify({ type: "ping" }));
          } catch {
            // ignore
          }
        }
      }, 60000);
    };
    ws.onmessage = (evt) => {
      let m;
      try {
        m = JSON.parse(evt.data);
      } catch {
        return;
      }
      switch (m.type) {
        case "frame":
          if (m.data_url) setFrameUrl(m.data_url);
          break;
        case "mv_start":
          if (m.url) setOpenedUrl(m.url);
          setInfoLine(`Opened ${m.url}`);
          break;
        case "mv_step": {
          const step = m.step || {};
          setRecSteps((prev) => [
            ...prev,
            {
              index: m.index,
              action: step.action,
              selector: step.selector ?? null,
              value: step.value ?? null,
              x_ratio: step.x_ratio ?? null,
              y_ratio: step.y_ratio ?? null,
              text: step.text ?? null,
              description: step.description ?? null,
              field_label: step.field_label ?? null,
              kind: step.kind ?? null,
              masked: !!step.masked,
              auto: !!step.auto,
              target: m.target || {},
              checked: true,
            },
          ]);
          if (step.action === "click" && m.target?.is_input && step.selector) {
            setActiveInputSelector(step.selector);
            fillInputRef.current?.focus();
          } else if (step.action === "press" || step.action === "fill") {
            const focus = m.focus || {};
            if (focus.is_input && focus.selector) {
              setActiveInputSelector(focus.selector);
              fillInputRef.current?.focus();
            } else {
              setActiveInputSelector(null);
            }
          }
          break;
        }
        case "mv_hover":
          setHoverBox(
            m.box
              ? { ...m.box, label: m.label ?? null, kind: m.kind ?? null }
              : null
          );
          break;
        case "info":
          if (m.message) setInfoLine(m.message);
          break;
        case "error":
          setRtError(m.message || "recorder error");
          break;
        case "done":
          setInfoLine("Recorder session ended.");
          break;
        default:
          break;
      }
    };
    ws.onclose = () => {
      wsRef.current = null;
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
    };
  }, []);

  const startRun = async ({ autoLogin = false, username = null, password = null } = {}) => {
    if (phase !== "form") return;
    setStartError("");
    setRtError("");
    setFrameUrl(null);
    setRecSteps([]);
    setInfoLine("");
    setActiveInputSelector(null);
    setFillValue("");
    setSessionAutoLogin(autoLogin);
    setPhase("starting");
    try {
      const res = await fetch(`${API_BASE_URL}testcases/recorder/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          auto_login: autoLogin,
          username: autoLogin ? username : null,
          password: autoLogin ? password : null,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.data?.session_id) {
        throw new Error(body?.detail || `HTTP ${res.status}`);
      }
      const sid = body.data.session_id;
      setSessionId(sid);
      setPhase("recording");
      openWs(sid);
    } catch (e) {
      setStartError(e.message || "Failed to start recorder");
      setPhase("form");
    }
  };

  // Close WS on unmount.
  useEffect(() => {
    return () => {
      try {
        wsRef.current?.close();
      } catch {
        // ignore
      }
      wsRef.current = null;
    };
  }, []);

  // Stream the cursor position (throttled) so the server can resolve the
  // element under it and send back a box to highlight. Uses the same img-rect
  // ratio math as onFrameClick, so the highlight aligns with where a click
  // would actually land.
  const onFrameHover = (e) => {
    if (!imgRef.current || !wsRef.current) return;
    if (wsRef.current.readyState !== WebSocket.OPEN) return;
    const now = typeof performance !== "undefined" ? performance.now() : Date.now();
    if (now - hoverThrottleRef.current < 80) return;
    hoverThrottleRef.current = now;
    const imgRect = imgRef.current.getBoundingClientRect();
    if (imgRect.width === 0 || imgRect.height === 0) return;
    const xRatio = Math.min(1, Math.max(0, (e.clientX - imgRect.left) / imgRect.width));
    const yRatio = Math.min(1, Math.max(0, (e.clientY - imgRect.top) / imgRect.height));
    try {
      wsRef.current.send(
        JSON.stringify({ type: "hover", x_ratio: xRatio, y_ratio: yRatio })
      );
    } catch {
      // ignore
    }
  };

  const clearHover = () => setHoverBox(null);

  // ---------- interactions ----------

  const onFrameClick = (e) => {
    if (!imgRef.current || !wsRef.current) return;
    const imgRect = imgRef.current.getBoundingClientRect();
    if (imgRect.width === 0 || imgRect.height === 0) return;
    const xRatio = Math.min(1, Math.max(0, (e.clientX - imgRect.left) / imgRect.width));
    const yRatio = Math.min(1, Math.max(0, (e.clientY - imgRect.top) / imgRect.height));
    setRtError("");
    setHoverBox(null);
    try {
      wsRef.current.send(
        JSON.stringify({ type: "click", x_ratio: xRatio, y_ratio: yRatio })
      );
    } catch {
      // ignore
    }
  };

  // Server falls back to document.activeElement when selector is null, so we
  // can apply even before the click round-trip has resolved a selector.
  const applyFill = () => {
    if (!wsRef.current) return;
    try {
      wsRef.current.send(
        JSON.stringify({
          type: "fill",
          selector: activeInputSelector,
          value: fillValue,
        })
      );
    } catch {
      // ignore
    }
    setFillValue("");
  };

  const sendKey = (key) => {
    if (!wsRef.current) return;
    try {
      wsRef.current.send(JSON.stringify({ type: "key", key }));
    } catch {
      // ignore
    }
  };

  const clearFill = () => {
    if (!wsRef.current) return;
    try {
      wsRef.current.send(
        JSON.stringify({
          type: "fill",
          selector: activeInputSelector,
          value: "",
        })
      );
    } catch {
      // ignore
    }
    setFillValue("");
  };

  const toggleStep = (i) => {
    setRecSteps((prev) =>
      prev.map((s, idx) => (idx === i ? { ...s, checked: !s.checked } : s))
    );
  };

  const toggleDynamic = (i) => {
    setRecSteps((prev) =>
      prev.map((s, idx) => {
        if (idx !== i) return s;
        const next = !s.is_dynamic;
        return {
          ...s,
          is_dynamic: next,
          param_name: next ? s.param_name || defaultParamName(s, idx) : s.param_name,
        };
      })
    );
  };

  const toggleCapture = (i) => {
    setRecSteps((prev) =>
      prev.map((s, idx) =>
        idx === i ? { ...s, capture_screenshot: !s.capture_screenshot } : s
      )
    );
  };

  const renameParam = (i, name) => {
    setRecSteps((prev) =>
      prev.map((s, idx) => (idx === i ? { ...s, param_name: name } : s))
    );
  };

  const toggleAll = (checked) => {
    setRecSteps((prev) => prev.map((s) => ({ ...s, checked })));
  };

  const deleteStep = (i) => {
    setRecSteps((prev) => prev.filter((_, idx) => idx !== i));
  };

  const refresh = () => {
    if (!wsRef.current) return;
    setRecSteps([]);
    setActiveInputSelector(null);
    setFillValue("");
    setRtError("");
    try {
      wsRef.current.send(JSON.stringify({ type: "refresh" }));
    } catch {
      // ignore
    }
  };

  const save = async () => {
    if (saving) return;
    setSaveError("");
    const xrayMode = !!selectedXrayTest;
    // Xray mode saves the committed (stashed) steps, plus any still-checked
    // buffer auto-assigned to the current step. Classic mode saves the checked
    // recorded steps as before.
    const bufferChecked = recSteps.filter((s) => s.checked);
    const stepsSource = xrayMode
      ? [
          ...committedSteps,
          ...bufferChecked.map((s) => ({
            ...s,
            source_step_no: currentXrayStep?.step_no ?? null,
          })),
        ]
      : bufferChecked;
    if (stepsSource.length === 0) {
      setSaveError(
        xrayMode
          ? "Stash at least one step against a test step before saving."
          : "Check at least one step to save."
      );
      return;
    }
    if (!name.trim()) {
      setSaveError(`${nameLabel} is required.`);
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}testcases`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          board_id: board.id,
          subcategorykey: subcategorykey || null,
          name: name.trim(),
          priority: "Medium",
          userkey: userkey || null,
          menu_label: "",
          menu_path: [],
          steps: stepsSource.map((s, idx) => {
            const paramName = s.is_dynamic
              ? s.param_name || defaultParamName(s, idx)
              : null;
            // Rewrite the display description so replay UI shows {{param}}
            // instead of the recorded literal (which is stripped below).
            const description = s.is_dynamic
              ? dynamicDescription(s, paramName)
              : s.description ?? null;
            return {
              action: s.action,
              selector: s.selector ?? null,
              // Drop the recorded literal for dynamic steps so it can't leak
              // into git history; the runner will prompt for it each replay.
              value: s.is_dynamic ? null : s.value ?? null,
              x_ratio: s.x_ratio ?? null,
              y_ratio: s.y_ratio ?? null,
              text: s.text ?? null,
              description,
              field_label: s.field_label ?? null,
              kind: s.kind ?? null,
              masked: !!s.masked,
              is_dynamic: !!s.is_dynamic,
              param_name: paramName,
              capture_screenshot: !!s.capture_screenshot,
              // Xray mapping: which Xray manual step this step implements.
              source_step_no: s.source_step_no ?? null,
            };
          }),
          source: {
            creation: "manual",
            mode: xrayMode ? "xray" : "manual",
            url: openedUrl,
            recorded_total: recSteps.length,
            saved: stepsSource.length,
            ...(xrayMode
              ? {
                  type: "xray",
                  xray_test_key: selectedXrayTest.key,
                  xray_test_id: selectedXrayTest.issue_id,
                  xray_story_key: presetStoryKey,
                  xray_steps: selectedXrayTest.steps,
                }
              : {}),
          },
          ai_context: "",
          auto_login: sessionAutoLogin,
          link_ticket_keys: linkTickets.map((t) => t.ticketkey),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail || `HTTP ${res.status}`);
      }
      try {
        wsRef.current?.send(JSON.stringify({ type: "stop" }));
      } catch {
        // ignore
      }
      try {
        wsRef.current?.close();
      } catch {
        // ignore
      }
      onCreate?.();
    } catch (e) {
      setSaveError(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  // ---------- render ----------

  return (
    <div
      className={
        maximized
          ? "fixed left-[1vw] top-[1vh] z-50 flex h-[98vh] w-[98vw] flex-col rounded-xl border border-slate-200 bg-card shadow-2xl"
          : "flex h-[calc(100vh-8rem)] flex-col"
      }
    >
      {/* Top ~20% — form + toolbar (hidden while maximized) */}
      {!maximized && (
      <div className="flex-none basis-1/5 border-b border-slate-200 bg-card p-4">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-700 hover:text-slate-900"
        >
          <Icons.ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <hr className="my-3 border-slate-200" />

        <div className="flex flex-wrap items-end gap-10">
          <div className="flex flex-col">
            <label className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
              {nameLabel}
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`${inputCls} max-w-md`}
              required
            />
          </div>
          <label
            className={`mb-2 inline-flex items-center gap-2 text-sm ${
              phase === "form"
                ? "cursor-pointer text-slate-700"
                : "cursor-not-allowed text-slate-400"
            }`}
          >
            <input
              type="checkbox"
              checked={autoLogin}
              onChange={(e) => setAutoLogin(e.target.checked)}
              disabled={phase !== "form"}
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
            />
            Auto login
          </label>
        </div>

        {phase === "form" && (
          <div className="mt-4 max-w-2xl">
            <TicketLinkField
              boardId={board.id}
              userkey={userkey}
              value={linkTickets}
              onChange={setLinkTickets}
              locked={lockTickets}
            />
          </div>
        )}

        {phase === "form" && presetStoryKey && (
          <div className="mt-4 max-w-2xl rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
                <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-violet-700">
                  Test
                </span>
                Record against a linked test
                <span className="font-normal text-slate-400">({presetStoryKey})</span>
              </span>
              <button
                type="button"
                onClick={() => loadXrayTests(presetStoryKey)}
                disabled={xrayLoading}
                className="inline-flex items-center gap-1 rounded-md border border-violet-300 bg-white px-2.5 py-1 text-xs font-medium text-violet-700 hover:bg-violet-50 disabled:opacity-50"
              >
                {xrayLoading ? "Checking Jira…" : xrayChecked ? "Re-check Jira" : "Check for tests"}
              </button>
            </div>
            {xrayError && (
              <div className="mt-2 rounded border border-rose-200 bg-rose-50 px-2 py-1 text-xs text-rose-700">
                {xrayError}
              </div>
            )}
            {xrayChecked && !xrayLoading && !xrayError && xrayData && (
              xrayData.tests?.length > 0 ? (
                <ul className="mt-2 space-y-1">
                  {xrayData.tests.map((t) => (
                    <li key={t.issue_id}>
                      <label
                        className={`flex cursor-pointer items-center gap-2 rounded border px-2.5 py-1.5 text-xs ${
                          selectedXrayTest?.issue_id === t.issue_id
                            ? "border-violet-400 bg-violet-50"
                            : "border-slate-200 bg-white"
                        }`}
                      >
                        <input
                          type="radio"
                          name="xraytest"
                          checked={selectedXrayTest?.issue_id === t.issue_id}
                          onChange={() => pickXrayTest(t)}
                          className="h-3.5 w-3.5 text-violet-600"
                        />
                        <span className="font-mono font-semibold text-violet-700">{t.key}</span>
                        <span className="flex-1 truncate text-slate-700">{t.summary}</span>
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600">
                          {t.steps?.length || 0} steps
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="mt-2 text-xs text-slate-500">
                  No tests linked to {presetStoryKey}. You can still record a normal test case.
                </div>
              )
            )}
            {selectedXrayTest && (
              <div className="mt-2 rounded border border-violet-200 bg-violet-50 px-2.5 py-1.5 text-[11px] text-violet-800">
                Recording against <span className="font-semibold">{selectedXrayTest.key}</span> — perform each
                test step in order and click <span className="font-semibold">Stash</span> to bind your steps to it.
                <button
                  type="button"
                  className="ml-1 underline"
                  onClick={() => {
                    setSelectedXrayTest(null);
                    setCommittedSteps([]);
                    setXrayStepIdx(0);
                  }}
                >
                  Clear
                </button>
              </div>
            )}
          </div>
        )}

        {startError && (
          <p className="mt-2 text-xs text-rose-600">{startError}</p>
        )}
        {phase === "recording" && infoLine && (
          <p className="mt-2 truncate text-xs text-slate-500">{infoLine}</p>
        )}
        {phase === "recording" && rtError && (
          <p className="mt-1 text-xs text-rose-600">{rtError}</p>
        )}
      </div>
      )}

      {/* Bottom ~80% — start button OR live viewport + steps sidebar */}
      {phase !== "recording" && (
        <div className="flex min-h-0 flex-1 items-center justify-center bg-slate-50">
          <button
            type="button"
            onClick={() => {
              // Auto-login now uses fixed server-side credentials (av /
              // Qwerty!23), so we no longer prompt for them here — just start
              // the session with auto_login on and let the backend sign in.
              startRun({ autoLogin });
            }}
            disabled={phase === "starting"}
            className="inline-flex h-14 items-center gap-3 rounded-xl bg-brand-700 px-8 text-base font-semibold text-white shadow-lg transition hover:bg-brand-800 disabled:opacity-60"
          >
            {phase === "starting" ? (
              <>
                <Icons.Spinner className="h-5 w-5 animate-spin" />
                Starting…
              </>
            ) : (
              <>
                <Icons.Play className="h-5 w-5" />
                Start Live Interaction
              </>
            )}
          </button>
        </div>
      )}

      {credOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
            onClick={() => setCredOpen(false)}
          />
          <div className={`relative z-10 flex w-full max-w-sm flex-col overflow-hidden ${DIALOG_HEIGHT} rounded-2xl bg-white shadow-xl ring-1 ring-slate-200`}>
            <div className="flex-none border-b border-slate-100 px-5 py-4">
              <h3 className="font-semibold text-slate-900">Login to record</h3>
              <p className="mt-1 text-xs text-slate-500">
                Enter credentials — they'll be used to auto-log-in before recording starts.
              </p>
            </div>
            <form
              autoComplete="off"
              onSubmit={(e) => {
                e.preventDefault();
                if (!credUser.trim() || !credPwd) return;
                setCredOpen(false);
                startRun({ autoLogin: true, username: credUser.trim(), password: credPwd });
              }}
              className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4"
            >
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                  User ID
                </label>
                <input
                  autoFocus
                  name="cq-userid"
                  autoComplete="off"
                  spellCheck={false}
                  value={credUser}
                  onChange={(e) => setCredUser(e.target.value)}
                  className={inputCls}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={credPwdVisible ? "text" : "password"}
                    name="cq-pwd"
                    autoComplete="new-password"
                    value={credPwd}
                    onChange={(e) => setCredPwd(e.target.value)}
                    className={[inputCls, "pr-10"].join(" ")}
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setCredPwdVisible((v) => !v)}
                    title={credPwdVisible ? "Hide password" : "Show password"}
                    className="absolute inset-y-0 right-2 flex items-center rounded p-1 text-slate-400 hover:text-slate-700"
                  >
                    <Icons.Eye className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setCredOpen(false)}
                  className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!credUser.trim() || !credPwd}
                  className="inline-flex items-center justify-center rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-800 disabled:opacity-60"
                >
                  Log in & start
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {phase === "recording" && (
        <div className="flex min-h-0 flex-1">
          <div className="flex min-w-0 flex-1 flex-col bg-slate-900">
            <div
              ref={viewportRef}
              className="relative flex flex-1 items-center justify-center overflow-hidden"
            >
              {frameUrl ? (
                <img
                  ref={imgRef}
                  src={frameUrl}
                  alt="Live browser view"
                  onClick={onFrameClick}
                  onMouseMove={onFrameHover}
                  onMouseLeave={clearHover}
                  draggable={false}
                  className="max-h-full max-w-full cursor-crosshair select-none object-contain"
                />
              ) : (
                <div className="flex flex-col items-center gap-2 text-slate-400">
                  <Icons.Spinner className="h-6 w-6 animate-spin" />
                  <p className="text-xs font-medium">Waiting for first frame…</p>
                </div>
              )}
              {frameUrl && hoverBox && imgRef.current && viewportRef.current && (() => {
                const ir = imgRef.current.getBoundingClientRect();
                const vr = viewportRef.current.getBoundingClientRect();
                if (ir.width === 0 || ir.height === 0) return null;
                const left = ir.left - vr.left + hoverBox.x_ratio * ir.width;
                const top = ir.top - vr.top + hoverBox.y_ratio * ir.height;
                const width = Math.max(2, hoverBox.w_ratio * ir.width);
                const height = Math.max(2, hoverBox.h_ratio * ir.height);
                const tag = [hoverBox.kind, hoverBox.label].filter(Boolean).join(" · ");
                return (
                  <div
                    className="pointer-events-none absolute z-10 rounded-sm bg-brand-400/15 ring-2 ring-brand-400 transition-all duration-75"
                    style={{ left, top, width, height }}
                  >
                    {tag && (
                      <span className="absolute -top-5 left-0 max-w-[16rem] truncate rounded bg-brand-600 px-1.5 py-0.5 text-[10px] font-semibold text-white shadow">
                        {tag}
                      </span>
                    )}
                  </div>
                );
              })()}
            </div>
            <div className="flex-none border-t border-slate-700 bg-slate-800 px-3 py-2">
              <div className="mb-1 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                <span>Fill input</span>
                <span className="font-mono normal-case text-slate-300">
                  {activeInputSelector || "click a form field or press Tab to focus"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  ref={fillInputRef}
                  value={fillValue}
                  onChange={(e) => setFillValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      applyFill();
                    }
                  }}
                  placeholder="Value…"
                  className="flex-1 rounded border border-slate-600 bg-slate-900 px-3 py-1.5 text-sm text-white outline-none placeholder:text-slate-500 focus:border-brand-500"
                />
                <button
                  type="button"
                  onClick={applyFill}
                  className="rounded bg-brand-700 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-800"
                >
                  Apply
                </button>
                <button
                  type="button"
                  onClick={clearFill}
                  title="Clear the focused field"
                  className="rounded border border-slate-600 bg-slate-900 px-3 py-1.5 text-sm font-medium text-slate-200 hover:bg-slate-700"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={() => sendKey("Tab")}
                  className="rounded border border-slate-600 bg-slate-900 px-3 py-1.5 text-sm font-medium text-slate-200 hover:bg-slate-700"
                >
                  ⇥ Tab
                </button>
                <button
                  type="button"
                  onClick={() => sendKey("Enter")}
                  className="rounded border border-slate-600 bg-slate-900 px-3 py-1.5 text-sm font-medium text-slate-200 hover:bg-slate-700"
                >
                  ↵ Enter
                </button>
                <button
                  type="button"
                  onClick={() => setMaximized((m) => !m)}
                  title={maximized ? "Restore" : "Maximize"}
                  className="rounded border border-slate-600 bg-slate-900 px-2 py-1.5 text-slate-200 hover:bg-slate-700"
                >
                  {maximized ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-4 w-4"
                    >
                      <path d="M8 3v3a2 2 0 0 1-2 2H3" />
                      <path d="M21 8h-3a2 2 0 0 1-2-2V3" />
                      <path d="M3 16h3a2 2 0 0 1 2 2v3" />
                      <path d="M16 21v-3a2 2 0 0 1 2-2h3" />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-4 w-4"
                    >
                      <path d="M3 8V5a2 2 0 0 1 2-2h3" />
                      <path d="M21 8V5a2 2 0 0 0-2-2h-3" />
                      <path d="M3 16v3a2 2 0 0 0 2 2h3" />
                      <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>

          <aside className="flex w-96 flex-none flex-col border-l border-slate-200 bg-card">
            {selectedXrayTest && (
              <div className="flex-none border-b border-violet-200 bg-violet-50/60">
                <div className="flex items-center justify-between px-4 py-2">
                  <span className="flex items-center gap-1.5 text-xs font-semibold text-violet-800">
                    <span className="rounded-full bg-violet-100 px-1.5 py-0.5 text-[9px] uppercase tracking-wider">
                      Test
                    </span>
                    {selectedXrayTest.key}
                  </span>
                  <span className="text-[11px] text-violet-700">
                    {new Set(committedSteps.map((s) => s.source_step_no)).size}/{xraySteps.length} steps stashed
                  </span>
                </div>

                {committedSteps.length > 0 && (
                  <div className="max-h-32 space-y-1 overflow-y-auto border-t border-violet-100 px-3 py-2">
                    {xraySteps
                      .filter((xs) => committedSteps.some((s) => s.source_step_no === xs.step_no))
                      .map((xs) => {
                        const grp = committedSteps.filter((s) => s.source_step_no === xs.step_no);
                        return (
                          <div
                            key={xs.step_no}
                            className="rounded border border-slate-200 bg-white px-2 py-1 text-[11px]"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="min-w-0 truncate font-semibold text-slate-700">
                                Step {xs.step_no}:{" "}
                                <span className="font-normal text-slate-600">{xs.action}</span>
                              </span>
                              <div className="flex flex-none items-center gap-2">
                                <span className="rounded-full bg-emerald-50 px-1.5 text-[10px] text-emerald-700">
                                  {grp.length} step{grp.length === 1 ? "" : "s"}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => unstashGroup(xs.step_no)}
                                  title="Unstash this step"
                                  className="text-slate-400 hover:text-rose-600"
                                >
                                  ✕
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}

                <div className="border-t border-violet-100 px-3 py-2">
                  {currentXrayStep ? (
                    <>
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-violet-600">
                        Currently doing — Step {currentXrayStep.step_no} of {xraySteps.length}
                      </div>
                      <div className="mt-0.5 text-xs text-slate-800">{currentXrayStep.action}</div>
                      {currentXrayStep.data && (
                        <div className="text-[11px] text-slate-500">Data: {currentXrayStep.data}</div>
                      )}
                      {currentXrayStep.result && (
                        <div className="text-[11px] text-slate-500">Expected: {currentXrayStep.result}</div>
                      )}
                    </>
                  ) : (
                    <div className="text-xs text-slate-600">All steps addressed.</div>
                  )}
                  <div className="mt-2 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={stashAgainstCurrentStep}
                      disabled={checkedCount === 0 || !currentXrayStep}
                      className="inline-flex flex-1 items-center justify-center gap-1 rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
                    >
                      Stash{checkedCount > 0 ? ` ${checkedCount} step${checkedCount === 1 ? "" : "s"}` : ""} → Step{" "}
                      {currentXrayStep?.step_no ?? "-"}
                    </button>
                    <select
                      value={xrayStepIdx}
                      onChange={(e) => setXrayStepIdx(Number(e.target.value))}
                      title="Jump to a different test step"
                      className="rounded border border-slate-300 bg-white px-1.5 py-1 text-[11px] text-slate-700"
                    >
                      {xraySteps.map((xs, i) => (
                        <option key={xs.step_no} value={i}>
                          Step {xs.step_no}
                        </option>
                      ))}
                    </select>
                  </div>
                  {allXrayStepsCovered && (
                    <div className="mt-1.5 text-[11px] font-medium text-emerald-700">
                      All test steps covered — you can Save.
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 text-sm font-bold uppercase tracking-wider text-slate-700">
              <span>{selectedXrayTest ? "Stash area" : "Recorded steps"} ({recSteps.length})</span>
              <div className="flex items-center gap-3 text-xs font-semibold normal-case">
                <button
                  type="button"
                  onClick={refresh}
                  title="Reset browser to home page and clear all recorded steps"
                  className="text-brand-700 hover:text-brand-900"
                >
                  Refresh
                </button>
                {recSteps.length > 0 && (
                  <>
                    <span className="text-slate-300">·</span>
                    <button
                      type="button"
                      onClick={() => toggleAll(true)}
                      className="text-slate-600 hover:text-slate-900"
                    >
                      All
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleAll(false)}
                      className="text-slate-600 hover:text-slate-900"
                    >
                      None
                    </button>
                  </>
                )}
              </div>
            </div>
            <p className="border-b border-slate-100 bg-slate-50 px-4 py-2 text-[11px] leading-snug text-slate-500">
              Tip — for any fill/type step, toggle the{" "}
              <span className="mx-0.5 rounded bg-amber-100 px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-800 ring-1 ring-amber-300">
                Dynamic
              </span>{" "}
              pill to be prompted for that value each time the test runs.
            </p>
            <ul className="min-h-0 flex-1 overflow-y-auto p-2">
              {recSteps.length === 0 ? (
                <li className="px-2 py-4 text-sm text-slate-400">
                  No steps yet — click the live view to start driving.
                </li>
              ) : (
                recSteps.map((s, i) => {
                  const dynamicable = canBeDynamic(s);
                  return (
                    <li
                      key={i}
                      className="border-b border-slate-100 py-1.5 last:border-b-0"
                    >
                      <div className="flex items-start gap-2">
                        <input
                          type="checkbox"
                          checked={s.checked}
                          onChange={() => toggleStep(i)}
                          className="mt-1 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
                        />
                        <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-brand-50 text-[11px] font-semibold text-brand-700">
                          {i + 1}
                        </span>
                        <span
                          className={
                            s.description
                              ? "min-w-0 flex-1 break-words text-sm text-slate-800"
                              : "min-w-0 flex-1 break-words font-mono text-xs text-slate-800"
                          }
                          title={legacyStepLabel(s)}
                        >
                          {stepLabel(s)}
                          {s.auto && (
                            <span
                              className="ml-1.5 inline-flex items-center rounded bg-violet-50 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-violet-700 ring-1 ring-inset ring-violet-600/20"
                              title="Auto-captured by the recorder — uncheck to skip"
                            >
                              auto
                            </span>
                          )}
                        </span>
                        {dynamicable && (
                          <button
                            type="button"
                            onClick={() => toggleDynamic(i)}
                            title={
                              s.is_dynamic
                                ? "Use recorded value on next run"
                                : "Ask for this value each time the test runs"
                            }
                            className={[
                              "mt-0.5 flex-none rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider transition",
                              s.is_dynamic
                                ? "bg-amber-100 text-amber-800 ring-1 ring-amber-300 hover:bg-amber-200"
                                : "bg-slate-100 text-slate-500 ring-1 ring-slate-200 hover:bg-slate-200",
                            ].join(" ")}
                          >
                            {s.is_dynamic ? "Dynamic" : "Static"}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => toggleCapture(i)}
                          title={
                            s.capture_screenshot
                              ? "Screenshot ON — click to disable capture for this step"
                              : "Screenshot OFF — click to capture a screenshot for this step"
                          }
                          className={[
                            "mt-0.5 flex-none rounded p-1 transition",
                            s.capture_screenshot
                              ? "bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200 hover:bg-emerald-100"
                              : "text-slate-400 hover:bg-slate-100 hover:text-slate-600",
                          ].join(" ")}
                        >
                          <Icons.Camera className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteStep(i)}
                          title="Delete this step"
                          className="mt-0.5 flex-none rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                        >
                          <Icons.Trash className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      {dynamicable && s.is_dynamic && (
                        <div className="mt-1 flex items-center gap-2 pl-9">
                          <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                            Prompt as
                          </label>
                          <input
                            value={s.param_name || ""}
                            onChange={(e) => renameParam(i, e.target.value)}
                            placeholder={defaultParamName(s, i)}
                            className="flex-1 rounded border border-slate-300 bg-white px-2 py-1 font-mono text-[11px] text-slate-800 outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/30"
                          />
                        </div>
                      )}
                    </li>
                  );
                })
              )}
            </ul>
            <div className="flex-none border-t border-slate-100 p-3">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={refresh}
                  disabled={saving}
                  title="Clear all steps and reset browser to home"
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={save}
                  disabled={
                    saving ||
                    (selectedXrayTest
                      ? committedSteps.length === 0 && checkedCount === 0
                      : checkedCount === 0)
                  }
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-800 disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <Icons.Spinner className="h-4 w-4 animate-spin" />
                      Saving…
                    </>
                  ) : selectedXrayTest ? (
                    `Save ${committedSteps.length + checkedCount} step${
                      committedSteps.length + checkedCount === 1 ? "" : "s"
                    }`
                  ) : (
                    `Save ${checkedCount} step${checkedCount === 1 ? "" : "s"}`
                  )}
                </button>
              </div>
              {saveError && (
                <p className="mt-2 text-xs text-rose-600">{saveError}</p>
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
